import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { getReservationCancel } from "../../services/welletService";
import WelletContext from "../../context/WelletContext";
import { getLocale } from "../../helpers/dates";
import ErrorPage from "../../screens/Error/ErrorPage";
import "./cancelOrder.css";

const Eyebrow = ({ text, variant }) => (
  <div className={`co__eyebrow ${variant === "success" ? "co__eyebrow--success" : ""}`}>
    <span className="co__eyebrow-rule" />
    <span className="co__eyebrow-diamond">◆</span>
    <span className="co__eyebrow-text">{text}</span>
    <span className="co__eyebrow-diamond">◆</span>
    <span className="co__eyebrow-rule" />
  </div>
);

const CancelOrder = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const welletContext = useContext(WelletContext);
  const language = useSelector((state) => state.app.language);
  const locale = getLocale(language);

  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [view, setView] = useState("confirm");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getReservationCancel(token);
        if (!cancelled) {
          setReservation(result);
          if (result?.status === "CANCELLED") setView("success");
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.response?.data?.errorCode || "UNKNOWN_ERROR");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleCancel = async () => {
    setView("cancelling");
    try {
      const response = await welletContext.apis.tickets.put(
        `/reservation/cancelReservation?token=${encodeURIComponent(token)}`
      );
      if (response.status === 200) {
        const firstName = (reservation?.customerName || "").split(" ")[0];
        navigate("/success-cancel", {
          state: {
            firstName,
            showName: reservation?.showName,
            externalPlatform: reservation?.externalPlatform || null,
          },
        });
      } else {
        setView("error");
      }
    } catch (err) {
      console.error(err);
      setView("error");
    }
  };

  if (loading) {
    return (
      <div className="co">
        <div className="co__main">
          <div className="co__container">
            <div className="co__spinner-container">
              <div className="co__spinner" />
              <div className="co__spinner-text">{t("cancelReservation.loading")}…</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    loadError ||
    !reservation ||
    (reservation.status !== "CONFIRMED" &&
      reservation.status !== "PENDING" &&
      reservation.status !== "CANCELLED")
  ) {
    return <ErrorPage />;
  }

  const firstName = (reservation.customerName || "").split(" ")[0];

  const venueName = reservation.showName;
  const supportEmail = reservation?.supportEmail || null;
  const externalPlatform = reservation?.externalPlatform;
  const showExternalPlatform =
    externalPlatform?.showPowerBy && externalPlatform?.name;

  const dateText =
    locale.code === "es"
      ? format(reservation.reservationDate, "EEEE d 'de' MMMM", { locale })
      : format(reservation.reservationDate, "EEEE, MMMM d", { locale });
  const timeText = format(reservation.reservationDate, "h:mm a", { locale });
  const guestsText = `${reservation.paxs} ${
    reservation.paxs > 1 ? t("people") : t("person")
  }`;
  const attributeText = reservation?.attribute
    ? t(`translateAttribute.${reservation.attribute}`)
    : null;

  return (
    <div className="co">
      <header className="co__header">
        <span className="co__brand">{venueName}</span>
      </header>

      <main className="co__main">
        <div className="co__container">
          {view === "confirm" && (
            <div className="co__state">
              <Eyebrow text={t("cancelReservation.eyebrow")} />
              <h1 className="co__title">
                {t("cancelReservation.headerTitle", { venue: venueName })}
              </h1>
              <p className="co__subtitle">{t("cancelReservation.headerSubtitle")}</p>

              <div className="co__data-card">
                <div className="co__data-row">
                  <span className="co__data-label">{t("cancelReservation.holder")}</span>
                  <span className="co__data-value">{reservation.customerName}</span>
                </div>
                <div className="co__data-row">
                  <span className="co__data-label">{t("cancelReservation.code")}</span>
                  <span className="co__data-value">{reservation.referenceCode}</span>
                </div>
                <div className="co__data-row">
                  <span className="co__data-label">{t("cancelReservation.guests")}</span>
                  <span className="co__data-value">{guestsText}</span>
                </div>
                <div className="co__data-row">
                  <span className="co__data-label">{t("cancelReservation.date")}</span>
                  <span className="co__data-value">{dateText}</span>
                </div>
                <div className="co__data-row">
                  <span className="co__data-label">{t("cancelReservation.time")}</span>
                  <span className="co__data-value">{timeText}</span>
                </div>
                {attributeText && (
                  <div className="co__data-row">
                    <span className="co__data-label">{t("cancelReservation.seat")}</span>
                    <span className="co__data-value">{attributeText}</span>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="co__btn-primary"
                onClick={handleCancel}
              >
                {t("cancelReservation.cancelButton")}
              </button>
            </div>
          )}

          {view === "cancelling" && (
            <div className="co__state">
              <div className="co__spinner-container">
                <div className="co__spinner" />
                <div className="co__spinner-text">
                  {t("cancelReservation.cancelling")}…
                </div>
              </div>
            </div>
          )}

          {view === "error" && (
            <div className="co__state">
              <div className="co__status-icon co__status-icon--error">
                <svg viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>
              <Eyebrow text={t("cancelReservation.errorEyebrow")} />
              <h1 className="co__title">{t("cancelReservation.errorTitle")}</h1>
              <p className="co__subtitle">{t("cancelReservation.errorSubtitle")}</p>
              <button
                type="button"
                className="co__btn-primary"
                onClick={handleCancel}
              >
                {t("cancelReservation.retry")}
              </button>
              {supportEmail && (
                <a href={`mailto:${supportEmail}`} className="co__btn-secondary">
                  {t("cancelReservation.contactSupport")}
                </a>
              )}
            </div>
          )}

          {view === "success" && (
            <div className="co__state">
              <div className="co__status-icon co__status-icon--success">
                <svg viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <Eyebrow
                text={t("cancelReservation.successEyebrow")}
                variant="success"
              />
              <h1 className="co__title">
                {t("cancelReservation.successTitle")}
              </h1>
              <p className="co__subtitle">
                {firstName
                  ? t("cancelReservation.successSubtitleNamed", { firstName })
                  : t("cancelReservation.successSubtitle")}
              </p>
            </div>
          )}
        </div>
      </main>

      <footer className="co__footer">
        {supportEmail && (
          <p className="co__footer-text">
            {t("cancelReservation.needHelp")}{" "}
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          </p>
        )}
        <p className="co__footer-powered">
          Powered by Wellet
          {showExternalPlatform ? ` · ${externalPlatform.name}` : ""}
        </p>
      </footer>
    </div>
  );
};

export default CancelOrder;
